import { useCallback, useMemo, type CSSProperties } from 'react';

import type {
  FieldSuggestion,
  ProcessType,
  SupplierMaterial,
} from '@frontend/api';
import { Select } from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import type { RootState } from 'store/configureStore';
import { useAppSelector } from 'store/hooks';
import { getMaterialOptionsForCascaderForSupplierMaterials } from 'store/materials/reducer';
import { useFeatureFlagValue } from 'store/session/hooks';

import MaterialSelector from './MaterialSelector';
import {
  filterMaterialFieldSuggestions,
  listParsableMaterialSuggestions,
} from './applyMaterialSuggestionDecoration';
import type {
  MaterialCascaderNode,
  MaterialSelectorChangeHandler,
} from './types';

type ProcessMaterialSelectorProps = {
  canSelectSubmenu?: boolean;
  disabled: boolean;
  materialFieldSuggestions?: FieldSuggestion[];
  menuPortalDestinationRef: React.RefObject<HTMLDivElement>;
  onChange: MaterialSelectorChangeHandler;
  placeholder: string;
  process?: ProcessType;
  selectedMaterialId: number | null | string;
  selectedSupplierMaterialId: number | string | null;
};

type ProcessMaterialSelectorOption = {
  supplierMaterialId: number;
  materialId: number;
  label: string;
  value: number;
  isSuggestion?: boolean;
  showSuggestionIcon?: boolean;
};

const ProcessMaterialSelector = ({
  canSelectSubmenu = false,
  disabled,
  materialFieldSuggestions,
  menuPortalDestinationRef,
  onChange, // passed as direct prop
  placeholder = 'Select material',
  process,
  selectedMaterialId,
  selectedSupplierMaterialId,
}: ProcessMaterialSelectorProps) => {
  const isCascaderMode = Boolean(
    process && !process.useListDisplayForProcessMaterials,
  );

  const isMaterialSuggestionsEnabled = useFeatureFlagValue(
    featureFlagKeys.isMaterialSuggestionsEnabled,
  );

  const supplierMaterialsForTree = useMemo(() => {
    if (!process?.processMaterials) return null;
    return process.processMaterials.map((pm) => ({
      ...pm.supplierMaterial,
      materialId: pm.supplierMaterial.material.id,
    }));
  }, [process]);

  const cascaderOptions: MaterialCascaderNode[] = useAppSelector(
    (state: RootState) =>
      supplierMaterialsForTree
        ? getMaterialOptionsForCascaderForSupplierMaterials(
            state,
            supplierMaterialsForTree,
          )
        : [],
  );

  const suggestedMaterials = useMemo((): {
    direct: Set<number>;
    family: Set<number>;
  } | null => {
    if (
      isCascaderMode ||
      !isMaterialSuggestionsEnabled ||
      !materialFieldSuggestions?.length
    ) {
      return null;
    }
    const entries = listParsableMaterialSuggestions(
      filterMaterialFieldSuggestions(materialFieldSuggestions),
    );
    if (!entries.length) return null;

    const direct = new Set<number>();
    const family = new Set<number>();
    for (const { target } of entries) {
      if (target.kind === 'material') {
        direct.add(Number(target.materialId));
        continue;
      }
      for (const matClass of cascaderOptions) {
        const familyNode = matClass.children?.find(
          (f) => String(f.value) === target.familyId,
        );
        if (familyNode) {
          for (const material of familyNode.children ?? []) {
            family.add(Number(material.value));
          }
          break;
        }
      }
    }
    return direct.size + family.size > 0 ? { direct, family } : null;
  }, [
    isCascaderMode,
    isMaterialSuggestionsEnabled,
    materialFieldSuggestions,
    cascaderOptions,
  ]);

  const processMaterialOptions = useMemo(() => {
    if (isCascaderMode || !process?.processMaterials) return [];

    return process.processMaterials
      .map((pm) => pm.supplierMaterial)
      .sort((a, b) =>
        a.displayName.localeCompare(b.displayName.toLocaleLowerCase()),
      )
      .map((supplierMaterial: SupplierMaterial) => {
        const materialId = supplierMaterial.material.id;
        const isDirect = suggestedMaterials?.direct.has(materialId) ?? false;
        const isFamily = suggestedMaterials?.family.has(materialId) ?? false;
        return {
          materialId,
          supplierMaterialId: supplierMaterial.id,
          value: supplierMaterial.id,
          label: supplierMaterial.displayName,
          ...((isDirect || isFamily) && { isSuggestion: true as const }),
          ...(isDirect && { showSuggestionIcon: true as const }),
        };
      });
  }, [isCascaderMode, process, suggestedMaterials]);

  const selectedListValue = useMemo(
    () =>
      processMaterialOptions.find(
        (processMaterial) =>
          processMaterial.supplierMaterialId === selectedSupplierMaterialId &&
          processMaterial.materialId === selectedMaterialId,
      ) ?? null,
    [processMaterialOptions, selectedMaterialId, selectedSupplierMaterialId],
  );

  const handleListSelectChange = useCallback(
    (option: ProcessMaterialSelectorOption | null) => {
      onChange({
        supplierMaterialId: option?.supplierMaterialId ?? null,
        materialId: option?.materialId ?? null,
      });
    },
    [onChange],
  );

  const selectStyles = useMemo(
    () => ({
      // NB: Preserved comment from 3 years ago: "fairly certain this doesn't affect the style"
      menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  return isCascaderMode ? (
    <MaterialSelector
      cascaderOptions={cascaderOptions}
      canSelectSubmenu={canSelectSubmenu}
      disabled={disabled}
      materialFieldSuggestions={materialFieldSuggestions}
      materialId={selectedMaterialId}
      onChange={onChange}
      placeholder={placeholder}
      supplierMaterialId={selectedSupplierMaterialId}
    />
  ) : (
    <Select
      onChange={handleListSelectChange}
      options={processMaterialOptions}
      value={selectedListValue}
      styles={selectStyles}
      menuPortalDestinationRef={menuPortalDestinationRef}
      menuPortalTarget={document.body}
      isDisabled={disabled}
      isSuggestionsEnabled={suggestedMaterials !== null}
      placeholder={placeholder}
      aria-label="Select material"
    />
  );
};

export default ProcessMaterialSelector;
